import type { RowData } from "@tanstack/react-table";
import { resolveColumnMeta } from "../columns/columnKinds";
import type { DataGridCell } from "../columns/types";
import type { DataGridCellChange, DataGridInstance } from "../types";
import type { EditableCell } from "./cellAccess";
import { toCellChange } from "./editActions";
import { parseEditorText, toCopyText } from "./editorValues";

/** A cell's value as copy writes it, which a paste or fill parses back. */
export const getCellCopyText = <TData extends RowData>(
    cell: DataGridCell<TData>,
) => {
    const { kind, editor } = resolveColumnMeta(
        cell.column.columnDef,
        cell.column.id,
    );
    return toCopyText({ kind, editor, value: cell.getValue() });
};

/** `text` through the cell's editor as a change; `null` when it doesn't parse. */
export const parseCellChange = <TData extends RowData>({
    instance,
    editable,
    text,
}: {
    instance: DataGridInstance<TData>;
    editable: EditableCell<TData>;
    text: string;
}): DataGridCellChange | null => {
    const parsed = parseEditorText({
        editor: editable.editor,
        text,
        today: instance.optionsRef.current.editing?.today,
    });
    return parsed.ok
        ? toCellChange({ ...editable, value: parsed.value })
        : null;
};
